import { forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  type LucideIcon,
  type LucideProps,
  AlertTriangle,
  CheckCircle2,
  Layers,
  ListChecks,
  Ruler,
  SlidersHorizontal,
  Store,
  Upload,
  Waves,
  Wrench,
} from "lucide-react";
import type { CennikFacety } from "@/api/schemas";
import { formatPocet } from "@/lib/format";
import { cn } from "@/lib/utils";
import { MenuPolozka, MenuStitok } from "@/components/zakazka/MenuPolozka";
import {
  TAB_LABELS,
  type CennikSidebarFiltre,
  type CennikTab,
  type PohladStranky,
} from "@/components/cennik/cennikZalozky";
import {
  POHLADY_UPOZORNENI,
  prepniPohlad,
} from "@/components/cennik/UpozorneniaCennika";

/**
 * Ľavé menu Cenníka (KOLO 199, okno B zo stránky „Okno cenníka") — ten
 * istý vzor ako prehľad Zákaziek: sekcie s počtami, čo treba doriešiť
 * a nastavenia na jednom mieste. Na mobile namiesto neho dve rozbaľovačky
 * (`CennikMenuMobil`).
 */

const SEKCIE = Object.keys(TAB_LABELS) as CennikTab[];

const IKONY: Record<CennikTab, LucideIcon> = {
  materialy: Layers,
  hrany: Ruler,
  kovanie: Wrench,
  operacie: ListChecks,
  tovar: Store,
};

/** Kde je čo riešiť, svieti jantárovo; pri nule zelená fajka. */
const IkonaPozor = forwardRef<SVGSVGElement, LucideProps>(function IkonaPozor(p, ref) {
  return <AlertTriangle ref={ref} {...p} className={cn(p.className, "text-amber-600")} />;
}) as LucideIcon;
const IkonaOk = forwardRef<SVGSVGElement, LucideProps>(function IkonaOk(p, ref) {
  return <CheckCircle2 ref={ref} {...p} className={cn(p.className, "text-emerald-600")} />;
}) as LucideIcon;

/** Odkaz z menu na nahrávanie cenníkov dodávateľov (Nastavenia, KOLO 170). */
export const ODKAZ_CENNIKY_DODAVATELOV = "/nastavenia?kategoria=ceny";

export type CennikMenuProps = {
  tab: CennikTab;
  pohlad: PohladStranky;
  /** Počty sekcií — materiálové z faset, Operácie zo zoznamu operácií. */
  pocty: Partial<Record<CennikTab, number>>;
  facety: CennikFacety | undefined;
  filtre: CennikSidebarFiltre;
  onSekcia: (t: CennikTab) => void;
  onPohlad: (p: PohladStranky) => void;
  onZmenaFiltre: (zmena: Partial<CennikSidebarFiltre>) => void;
};

/** Pohľady „Treba doriešiť" majú zmysel len v zozname materiálov. */
function ukazDoriesit(p: Pick<CennikMenuProps, "tab" | "pohlad">): boolean {
  return p.pohlad === "cennik" && p.tab !== "operacie";
}

export function CennikMenu(props: CennikMenuProps): JSX.Element {
  const { tab, pohlad, pocty, facety, filtre, onSekcia, onPohlad, onZmenaFiltre } = props;
  const navigate = useNavigate();
  return (
    <aside
      aria-label="Menu cenníka"
      className="hidden space-y-5 border-r pr-3 md:sticky md:top-0 md:block md:self-start"
    >
      <div className="space-y-1">
        <MenuStitok>Sekcia</MenuStitok>
        {SEKCIE.map((t) => (
          <MenuPolozka
            key={t}
            ikona={IKONY[t]}
            nazov={TAB_LABELS[t]}
            pocet={pocty[t]}
            aktivna={pohlad === "cennik" && tab === t}
            oznacenie="current"
            onClick={() => onSekcia(t)}
          />
        ))}
      </div>

      {ukazDoriesit(props) ? (
        <div className="space-y-1">
          <MenuStitok>Treba doriešiť</MenuStitok>
          {POHLADY_UPOZORNENI.map((p) => {
            const pocet = facety ? p.pocet(facety) : undefined;
            const zapnuty = filtre[p.kluc];
            return (
              <MenuPolozka
                key={p.kluc}
                ikona={pocet === 0 && !zapnuty ? IkonaOk : IkonaPozor}
                nazov={p.label}
                pocet={pocet}
                aktivna={zapnuty}
                oznacenie="pressed"
                title={`${p.title}${zapnuty ? " — klik zruší" : ""}`}
                onClick={() => onZmenaFiltre(prepniPohlad(filtre, p.kluc))}
              />
            );
          })}
        </div>
      ) : null}

      <div className="space-y-1 border-t pt-3">
        <MenuStitok>Nastavenia</MenuStitok>
        <MenuPolozka
          ikona={SlidersHorizontal}
          nazov="Nastavenia cien"
          pocet={undefined}
          bezPoctu
          aktivna={pohlad === "nastavenia-cien"}
          oznacenie="current"
          title="Marže kategórií — práca, réžia, nadmiera"
          onClick={() => onPohlad("nastavenia-cien")}
        />
        <MenuPolozka
          ikona={Waves}
          nazov="Štruktúry"
          pocet={undefined}
          bezPoctu
          aktivna={pohlad === "struktury"}
          oznacenie="current"
          title="Katalóg povrchových štruktúr"
          onClick={() => onPohlad("struktury")}
        />
        <MenuPolozka
          ikona={Upload}
          nazov="Cenníky dodávateľov"
          pocet={undefined}
          bezPoctu
          aktivna={false}
          oznacenie="current"
          title="Nahrať nový cenník dodávateľa — Nastavenia → Ceny a cenníky"
          onClick={() => navigate(ODKAZ_CENNIKY_DODAVATELOV)}
        />
      </div>
    </aside>
  );
}

/** Hodnota rozbaľovačky Sekcia na mobile — sekcia alebo pohľad Nastavení. */
const DODAVATELIA = "__cenniky_dodavatelov__";
const ZIADNY = "__ziadny__";

/** Mobil (< md): namiesto menu dve rozbaľovačky ako v Zákazkách (KOLO 174). */
export function CennikMenuMobil(props: CennikMenuProps): JSX.Element {
  const { tab, pohlad, pocty, facety, filtre, onSekcia, onPohlad, onZmenaFiltre } = props;
  const navigate = useNavigate();
  const zapnuty = POHLADY_UPOZORNENI.find((p) => filtre[p.kluc])?.kluc ?? ZIADNY;
  return (
    <div className="flex flex-col gap-2 md:hidden">
      <div className="flex min-w-0 items-center gap-2">
        <label htmlFor="cennik-sekcia-mobil" className="w-16 text-sm text-muted-foreground">
          Sekcia
        </label>
        <select
          id="cennik-sekcia-mobil"
          value={pohlad === "cennik" ? tab : pohlad}
          onChange={(e) => {
            const v = e.target.value;
            if (v === DODAVATELIA) navigate(ODKAZ_CENNIKY_DODAVATELOV);
            else if (v === "nastavenia-cien" || v === "struktury") onPohlad(v);
            else onSekcia(v as CennikTab);
          }}
          className="h-9 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-sm"
        >
          {SEKCIE.map((t) => (
            <option key={t} value={t}>
              {TAB_LABELS[t]}
              {pocty[t] !== undefined ? ` (${formatPocet(pocty[t]!)})` : ""}
            </option>
          ))}
          <optgroup label="Nastavenia">
            <option value="nastavenia-cien">Nastavenia cien</option>
            <option value="struktury">Štruktúry</option>
            <option value={DODAVATELIA}>Cenníky dodávateľov</option>
          </optgroup>
        </select>
      </div>
      {ukazDoriesit(props) ? (
        <div className="flex min-w-0 items-center gap-2">
          <label htmlFor="cennik-doriesit-mobil" className="w-16 text-sm text-muted-foreground">
            Doriešiť
          </label>
          <select
            id="cennik-doriesit-mobil"
            value={zapnuty}
            onChange={(e) => {
              const v = e.target.value;
              onZmenaFiltre(
                v === ZIADNY
                  ? { nedokoncene: false, nezaradene: false, skontrolujCenu: false }
                  : prepniPohlad(
                      { ...filtre, nedokoncene: false, nezaradene: false, skontrolujCenu: false },
                      v as (typeof POHLADY_UPOZORNENI)[number]["kluc"],
                    ),
              );
            }}
            className="h-9 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-sm"
          >
            <option value={ZIADNY}>Všetko</option>
            {POHLADY_UPOZORNENI.map((p) => (
              <option key={p.kluc} value={p.kluc}>
                {p.label}
                {facety ? ` (${formatPocet(p.pocet(facety))})` : ""}
              </option>
            ))}
          </select>
        </div>
      ) : null}
    </div>
  );
}
